import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Flex, Heading, Text, Input, InputGroup, InputLeftElement,
  SimpleGrid, Button, ButtonGroup, Tag, Card, CardBody, Badge, Image,
  useColorModeValue, Skeleton, SkeletonText, IconButton,
  Menu, MenuButton, MenuList, MenuItem, Tooltip, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Stat, StatLabel,
  StatNumber, HStack, Wrap, WrapItem
} from '@chakra-ui/react';
import { 
  Search, Plus, Zap, Download, Edit, Trash2, 
  MoreVertical, Brain, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import { getModels, deleteModel } from '../api/modelService';
import useModelThumbnail from '../hooks/useModelThumbnail';
import { useAuth } from '../context/AuthContext';


// Category color mapping
const categoryColors = {
  'Classification': 'blue',
  'NLP': 'purple',
  'Time-Series': 'orange',
  'Audio': 'pink',
  'Recommender': 'green',
  'Computer Vision': 'cyan',
  'Regression': 'yellow',
};

// Framework icons/colors
const frameworkColors = {
  'scikit-learn': 'orange',
  'PyTorch': 'red',
  'TensorFlow': 'yellow',
  'AutoGluon': 'teal',
};

// Input type icons
const inputTypeIcons = {
  'image': '🖼️',
  'audio': '🔊',
  'text': '📝',
  'tabular': '📊',
};

const statusColors = { pending: 'yellow', approved: 'green', rejected: 'red' };


// ============ MODEL CARD ============

const ModelCard = ({ model, onDelete, canManage }) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const metricBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const thumbnailSrc = useModelThumbnail(model);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await deleteModel(model.id, token);
      toast({ title: 'Model deleted', status: 'success', duration: 3000 });
      onDelete(model.id);
      onClose();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, status: 'error', duration: 3000 });
    }
  };

  const accuracy = model.accuracy != null ? (model.accuracy * 100).toFixed(1) : null;
  const catColor = categoryColors[model.category] || 'gray';
  const fwColor = frameworkColors[model.framework] || 'gray';
  const inputIcon = inputTypeIcons[model.input_type] || '📦';

  return (
    <>
      <Card
        direction="column"
        overflow="hidden"
        variant="outline"
        borderRadius="xl"
        boxShadow="sm"
        borderColor={borderColor}
        bg={cardBg}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: 'translateY(-6px)',
          boxShadow: 'xl',
          borderColor: 'teal.300'
        }}
        cursor="pointer"
        position="relative"
      >
        {/* Thumbnail / Gradient Header */}
        <Box
          h="140px"
          bg={thumbnailSrc ? 'transparent' : `linear-gradient(135deg, var(--chakra-colors-${catColor}-400), var(--chakra-colors-${catColor}-600))`}
          position="relative"
          overflow="hidden"
          onClick={() => navigate(`/models/${model.id}`)}
        >
          {thumbnailSrc ? (
            <Image
              src={thumbnailSrc}
              alt={model.title}
              w="100%" h="100%" objectFit={model.input_type === 'image' ? 'contain' : 'cover'}
              fallback={
                <Flex h="100%" align="center" justify="center"
                  bg={`linear-gradient(135deg, var(--chakra-colors-${catColor}-400), var(--chakra-colors-${catColor}-600))`}>
                  <Brain size={48} color="white" opacity={0.6} />
                </Flex>
              }
            />
          ) : (
            <Flex h="100%" align="center" justify="center">
              <Box opacity={0.3}>
                <Brain size={64} color="white" />
              </Box>
            </Flex>
          )}

          {/* Floating badges */}
          <HStack position="absolute" top={3} left={3} spacing={2}>
            <Badge colorScheme={catColor} px={2} py={1} borderRadius="md"
              fontSize="xs" fontWeight="bold" textTransform="uppercase"
              backdropFilter="blur(4px)" bg={`${catColor}.500`} color="white">
              {model.category}
            </Badge>
            {model.status && model.status !== 'approved' && (
              <Badge colorScheme={statusColors[model.status]} variant="solid" px={2} py={1}
                borderRadius="md" fontSize="xs" textTransform="uppercase">
                {model.status}
              </Badge>
            )}
          </HStack>

          {/* Input type icon */}
          <Tooltip label={`Input: ${model.input_type}`}>
            <Box position="absolute" top={3} right={3}
              bg="blackAlpha.600" borderRadius="full" p={1.5}
              fontSize="md" lineHeight={1}>
              {inputIcon}
            </Box>
          </Tooltip>

          {/* Action menu */}
          {canManage && (
            <Box position="absolute" bottom={2} right={2} onClick={e => e.stopPropagation()}>
              <Menu>
                <MenuButton as={IconButton} icon={<MoreVertical size={16} />}
                  size="xs" variant="solid" bg="blackAlpha.600" color="white"
                  _hover={{ bg: 'blackAlpha.800' }} borderRadius="full" />
                <MenuList fontSize="sm">
                  <MenuItem icon={<Edit size={14} />} onClick={() => navigate(`/models/${model.id}/edit`)}>
                    Edit
                  </MenuItem>
                  <MenuItem icon={<Trash2 size={14} />} color="red.500" onClick={onOpen}>
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          )}
        </Box>

        {/* Card Body */}
        <CardBody p={4} onClick={() => navigate(`/models/${model.id}`)}>
          <Heading size="sm" noOfLines={1} mb={1}>{model.title}</Heading>
          <Text fontSize="xs" noOfLines={2} color={subtextColor} mb={3} minH="32px">
            {model.description || 'No description provided.'}
          </Text>
          
          {/* Metrics Row */}
          <HStack spacing={3} mb={3} flexWrap="wrap">
            {accuracy && (
              <HStack spacing={1} bg={metricBg} px={2} py={1} borderRadius="md">
                <Zap size={12} color="var(--chakra-colors-orange-400)" />
                <Text fontSize="xs" fontWeight="bold" color="orange.500">{accuracy}%</Text>
              </HStack>
            )}
            <HStack spacing={1} bg={metricBg} px={2} py={1} borderRadius="md">
              <Badge colorScheme={fwColor} variant="subtle" fontSize="2xs">
                {model.framework}
              </Badge>
            </HStack>
            {model.has_model_file && (
              <HStack spacing={1} bg={metricBg} px={2} py={1} borderRadius="md">
                <Download size={10} />
                <Text fontSize="2xs">{model.download_count || 0}</Text>
              </HStack>
            )}
          </HStack>

          {/* Tags */}
          {model.tags && model.tags.length > 0 && (
            <Wrap spacing={1}>
              {model.tags.slice(0, 3).map((tag, i) => (
                <WrapItem key={i}>
                  <Tag size="sm" variant="subtle" colorScheme="gray" fontSize="2xs">
                    {tag}
                  </Tag>
                </WrapItem>
              ))}
              {model.tags.length > 3 && (
                <WrapItem>
                  <Tag size="sm" variant="subtle" colorScheme="gray" fontSize="2xs">
                    +{model.tags.length - 3}
                  </Tag>
                </WrapItem>
              )}
            </Wrap>
          )}
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Model</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete <strong>{model.title}</strong>? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};


// ============ SKELETON CARD ============

const SkeletonCard = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  return (
    <Card variant="outline" borderRadius="xl" bg={cardBg} overflow="hidden">
      <Skeleton h="140px" />
      <CardBody p={4}>
        <Skeleton h="20px" w="70%" mb={2} />
        <SkeletonText noOfLines={2} spacing={2} mb={3} />
        <Skeleton h="20px" w="50%" />
      </CardBody>
    </Card>
  );
};


// ============ MAIN PAGE ============

const ModelsPage = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  // ?view=pending opens the review queue (the sidebar's "Model Verification" link)
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'all'); // 'all' | 'mine' | 'pending'
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const headerBg = useColorModeValue(
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #434343 0%, #1a1a2e 100%)'
  );
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const statBg = useColorModeValue('white', 'gray.800');
  const pillBg = useColorModeValue('white', 'gray.700');
  const pillColor = useColorModeValue('gray.700', 'gray.200');
  const pillBorderColor = useColorModeValue('gray.200', 'gray.600');
  const pillHoverBg = useColorModeValue('gray.100', 'gray.600');

  const categories = [
    'All', 'Classification', 'NLP', 'Time-Series', 'Audio',
    'Recommender', 'Computer Vision', 'Regression'
  ];

  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 12 };
      if (searchQuery) params.search = searchQuery;
      if (activeCategory !== 'All') params.category = activeCategory;
      if (view === 'mine') params.mine = true;
      if (view === 'pending') params.status = 'pending';

      const data = await getModels(params);
      setModels(data.models || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch models:', err);
      toast({ title: 'Failed to load models', status: 'error', duration: 3000 });
    }
    setLoading(false);
  }, [page, searchQuery, activeCategory, view, toast]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
    }, 350);
    setSearchTimeout(timeout);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setPage(1);
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
    setPage(1);
    setSearchParams(nextView === 'all' ? {} : { view: nextView });
  };

  // Follow the URL when the sidebar link is clicked while this page is already open
  useEffect(() => {
    const fromUrl = searchParams.get('view') || 'all';
    setView((current) => (current === fromUrl ? current : fromUrl));
  }, [searchParams]);

  const handleDeleteModel = (deletedId) => {
    setModels(prev => prev.filter(m => m.id !== deletedId));
    setTotal(prev => prev - 1);
  };

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

  // Counted from the current page of results
  const withDemo = models.filter(m => m.demo_available).length;

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />

      <Box 
        pl={sidebarCollapsed ? "60px" : "250px"} 
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        {/* Hero Header */}
        <Box
          bg={headerBg}
          px={8} py={10}
          color="white"
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box position="absolute" top="-20px" right="-20px" opacity={0.08}>
            <Brain size={240} />
          </Box>

          <Flex justify="space-between" align="flex-start" position="relative" zIndex={1}>
            <Box maxW="600px">
              <HStack mb={2}>
                <Brain size={28} />
                <Heading size="xl">ML Models</Heading>
              </HStack>
              <Text fontSize="md" opacity={0.9} lineHeight="tall">
                Browse, upload, and interact with machine learning models.
                Run live demos, explore model architectures, and download trained models.
              </Text>
            </Box>

            <Button
              leftIcon={<Plus size={18} />}
              bg="whiteAlpha.200"
              color="white"
              _hover={{ bg: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
              _active={{ bg: 'whiteAlpha.400' }}
              size="lg"
              borderRadius="xl"
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor="whiteAlpha.300"
              transition="all 0.2s"
              onClick={() => navigate('/models/upload')}
            >
              Upload Model
            </Button>
          </Flex>

          {/* Stats Row */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={6} position="relative" zIndex={1}>
            <Stat bg="whiteAlpha.100" p={3} borderRadius="xl" backdropFilter="blur(10px)">
              <StatLabel fontSize="xs" opacity={0.8}>Total Models</StatLabel>
              <StatNumber fontSize="2xl">{total}</StatNumber>
            </Stat>
            <Stat bg="whiteAlpha.100" p={3} borderRadius="xl" backdropFilter="blur(10px)">
              <StatLabel fontSize="xs" opacity={0.8}>Live Demos (this page)</StatLabel>
              <StatNumber fontSize="2xl">{withDemo}</StatNumber>
            </Stat>
            <Stat bg="whiteAlpha.100" p={3} borderRadius="xl" backdropFilter="blur(10px)">
              <StatLabel fontSize="xs" opacity={0.8}>Categories</StatLabel>
              <StatNumber fontSize="2xl">{categories.length - 1}</StatNumber>
            </Stat>
            <Stat bg="whiteAlpha.100" p={3} borderRadius="xl" backdropFilter="blur(10px)">
              <StatLabel fontSize="xs" opacity={0.8}>Current View</StatLabel>
              <StatNumber fontSize="2xl">{activeCategory}</StatNumber>
            </Stat>
          </SimpleGrid>
        </Box>

        {/* Content Area */}
        <Box px={8} mt={-4} position="relative" zIndex={2}>
          {/* Search Bar */}
          <Box bg={statBg} borderRadius="xl" boxShadow="lg" p={1} mb={6}>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none" h="full">
                <Search color="gray" size={20} />
              </InputLeftElement>
              <Input
                placeholder="Search models by name, description, or tags..."
                border="none"
                focusBorderColor="transparent"
                _focus={{ boxShadow: 'none' }}
                fontSize="md"
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Box>

          {/* Category Pills */}
          <Flex mb={6} overflowX="auto" pb={2} gap={2}
            css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            {categories.map(category => {
              const isActive = activeCategory === category;
              const color = category === 'All' ? 'teal' : (categoryColors[category] || 'gray');
              return (
                <Tag
                  key={category}
                  py={2} px={4}
                  borderRadius="full"
                  cursor="pointer"
                  bg={isActive ? `${color}.500` : pillBg}
                  color={isActive ? 'white' : pillColor}
                  boxShadow={isActive ? `0 4px 12px var(--chakra-colors-${color}-200)` : 'sm'}
                  border="1px solid"
                  borderColor={isActive ? 'transparent' : pillBorderColor}
                  onClick={() => handleCategoryChange(category)}
                  _hover={{
                    bg: isActive ? `${color}.600` : pillHoverBg,
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s"
                  fontWeight="medium"
                  fontSize="sm"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {category}
                </Tag>
              );
            })}
          </Flex>

          {/* Results Header */}
          <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {loading ? 'Loading...' : `${total} model${total !== 1 ? 's' : ''} found`}
            </Text>
            {user && (
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button onClick={() => handleViewChange('all')} isActive={view === 'all'}>All models</Button>
                <Button onClick={() => handleViewChange('mine')} isActive={view === 'mine'}>My uploads</Button>
                {['admin', 'expert', 'super_expert'].includes(user.role) && (
                  <Button onClick={() => handleViewChange('pending')} isActive={view === 'pending'}>
                    Pending review
                  </Button>
                )}
              </ButtonGroup>
            )}
          </Flex>

          {/* Models Grid */}
          {loading ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </SimpleGrid>
          ) : models.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py={20}>
              <Box opacity={0.3} mb={4}>
                <Brain size={80} />
              </Box>
              <Heading size="md" mb={2} color="gray.500">No models found</Heading>
              <Text color="gray.400" mb={6} textAlign="center">
                {searchQuery || activeCategory !== 'All'
                  ? 'Try adjusting your search or filter.'
                  : 'Upload your first model to get started!'}
              </Text>
              <Button
                leftIcon={<Plus size={16} />}
                colorScheme="teal"
                onClick={() => navigate('/models/upload')}
              >
                Upload Model
              </Button>
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
              {models.map((model) => (
                <ModelCard
                  key={model.id}
                  model={model}
                  onDelete={handleDeleteModel}
                  canManage={
                    user && (user.role === 'admin' || user.userId === model.uploaded_by)
                  }
                />
              ))}
            </SimpleGrid>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justify="center" align="center" mt={8} gap={3}>
              <IconButton
                icon={<ChevronLeft size={18} />}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                isDisabled={page === 1}
                variant="outline"
                borderRadius="full"
                size="sm"
              />
              <HStack spacing={1}>
                {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={page === pageNum ? 'solid' : 'ghost'}
                      colorScheme={page === pageNum ? 'teal' : 'gray'}
                      borderRadius="full"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </HStack>
              <IconButton
                icon={<ChevronRight size={18} />}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
                variant="outline"
                borderRadius="full"
                size="sm"
              />
            </Flex>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default ModelsPage;
